<template>
  <div v-if="hasError" class="error-boundary">
    <div class="error-boundary-inner">
      <div class="error-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <h3>Something went wrong</h3>
      <p class="error-message-text">
        An unexpected error occurred on this page. The issue has been logged automatically.
      </p>
      <div v-if="isDev && errorMessage" class="error-details">
        <code>{{ errorMessage }}</code>
      </div>
      <div class="error-actions">
        <button class="btn-retry" @click="reset">
          <i class="fas fa-redo"></i> Try Again
        </button>
        <button class="btn-reload" @click="hardReload">
          <i class="fas fa-sync"></i> Reload Page
        </button>
      </div>
    </div>
  </div>
  <slot v-else></slot>
</template>

<script>
export default {
  name: 'ErrorBoundary',

  data() {
    return {
      hasError: false,
      errorMessage: null,
    };
  },

  computed: {
    isDev() {
      return import.meta.env?.DEV || process.env?.NODE_ENV === 'development';
    },
  },

  methods: {
    reset() {
      this.hasError = false;
      this.errorMessage = null;
    },

    hardReload() {
      window.location.reload();
    },
  },

  errorCaptured(err, instance, info) {
    this.hasError = true;
    this.errorMessage = err?.message || String(err);

    // Report to Sentry if available
    try {
      if (window.__SENTRY__ || window.Sentry) {
        const Sentry = window.__SENTRY__ || window.Sentry;
        Sentry.withScope?.((scope) => {
          scope.setExtra('componentInfo', info);
          scope.setExtra('componentName', instance?.$options?.name || 'Unknown');
          Sentry.captureException(err);
        });
      }
    } catch (_) {
      // Sentry not available — ignore
    }

    // Prevent error from bubbling further
    return false;
  },
};
</script>

<style scoped>
.error-boundary {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 2rem;
}

.error-boundary-inner {
  text-align: center;
  max-width: 480px;
}

.error-icon {
  font-size: 3rem;
  color: #e74c3c;
  margin-bottom: 1rem;
}

.error-boundary-inner h3 {
  font-size: 1.4rem;
  color: #2c3e50;
  margin: 0 0 0.5rem;
}

.error-message-text {
  color: #6c757d;
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
}

.error-details {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
  text-align: left;
  overflow-x: auto;
}

.error-details code {
  font-size: 0.8rem;
  color: #e74c3c;
}

.error-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

.btn-retry,
.btn-reload {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s;
}

.btn-retry {
  background: var(--color-primary, #2ecc71);
  color: white;
}

.btn-reload {
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
}

.btn-retry:hover,
.btn-reload:hover {
  opacity: 0.85;
}
</style>
